<script lang="ts" setup>
import Co2AverageForm from '@/components/co2quiz/Co2AverageForm.vue';
import ListFilter from '@/components/common/ListFilter.vue';
import CurrentLanguage from '@/components/CurrentLanguage.vue';
import { Co2CountryAverage } from '@/models/co2-quiz.model';
import { useEvents, Event } from '@/services/events.composable';
import { Mutations, ShiftCMSState } from '@/store';
import {
  Co2QuizStoreActions,
  Co2QuizStoreState,
} from '@/stores/co2-quiz.store';
import { computed, markRaw, onBeforeUnmount, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { countryName, OneEarth } from '@/models/country.model';

const store = useStore<ShiftCMSState & { co2: Co2QuizStoreState }>();
const events = useEvents();

const questions = computed<Co2CountryAverage[]>(() => store.state.co2.averages);

const update = () => {
  store.dispatch(Co2QuizStoreActions.GetCo2Averages);
};

onMounted(() => {
  events.on(Event.LanguageChanged, update);
  update();
});

onBeforeUnmount(() => {
  events.off(Event.LanguageChanged, update);
});

const headers = [
  { title: 'ID', key: 'id', sortable: false, width: '5%' },
  { title: 'Country', key: 'country', width: '15%' },
  { title: 'Category', key: 'categories', width: '25%' },
  { title: 'Average', key: 'value', width: '20%' },
  { title: 'State', key: 'translated', sortable: false, width: '35%' },
];

const editAverage = (_: Event, { item }: { item: Co2CountryAverage }) => {
  store.commit(Mutations.ShowDrawer, {
    component: markRaw(Co2AverageForm),
    data: item,
  });
};

const addAverage = () => {
  store.commit(Mutations.ShowDrawer, {
    component: markRaw(Co2AverageForm),
  });
};

const filterTitle = ref<string>('');
const filteredAverages = computed(() =>
  questions.value.filter((v) =>
    filterTitle.value
      ? v.country.toLowerCase().indexOf(filterTitle.value.toLowerCase()) !==
          -1 ||
        v.category.title
          .toLowerCase()
          .indexOf(filterTitle.value.toLowerCase()) !== -1
      : true,
  ),
);

function updateFilter(filters: { filter: string; filterTitle: string }) {
  filterTitle.value = filters.filterTitle;
}
</script>
<template>
  <v-container>
    <h2>CO₂ Averages <CurrentLanguage /></h2>
    <ListFilter @filter="updateFilter" />
    <v-data-table
      density="compact"
      :headers="headers"
      :items="filteredAverages"
      @click:row="editAverage"
      itemsPerPage="25"
      hover
    >
      <template v-slot:item.categories="{ item }">
        {{ item.category.title }}
      </template>
      <template v-slot:item.country="{ item }">
        <v-chip v-if="item.country === OneEarth" density="compact" color="info"
          >One Earth</v-chip
        >
        <span v-else>{{ countryName(item.country) }}</span>
      </template>
    </v-data-table>

    <v-btn @click="addAverage" color="success" class="mr-4">Add Average</v-btn>
  </v-container>
</template>
